import { differenceInYears, isValid, parseISO } from 'date-fns';

export function getStudentInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.charAt(0) ?? '') : '';
  const initials = `${first}${last}`.toUpperCase();
  return initials || '?';
}

/**
 * Whole years old on `asOfDate` (yyyy-MM-dd); null when dob is missing/invalid.
 * Only the calendar-day part of `dob` is used so a UTC-midnight timestamp
 * doesn't shift the birthday in timezones ahead of UTC.
 */
export function getAgeOnDate(
  dob: string | null | undefined,
  asOfDate: string,
): number | null {
  if (!dob) return null;
  const birth = parseISO(dob.slice(0, 10));
  const asOf = parseISO(asOfDate.slice(0, 10));
  if (!isValid(birth) || !isValid(asOf)) return null;
  const years = differenceInYears(asOf, birth);
  return years < 0 ? null : years;
}

interface NameParts {
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  studentName?: string | null;
}

function splitDisplayName(value: string | null | undefined): Array<string> {
  return (value ?? '').trim().split(/\s+/).filter(Boolean);
}

function getDisplayName(student: NameParts): string {
  const primary = student.name?.trim();
  if (primary) return primary;
  return student.studentName?.trim() ?? '';
}

/** Prefer explicit lastName; otherwise last token of the display name. */
export function getAttendanceSortLastName(student: NameParts): string {
  const explicit = student.lastName?.trim();
  if (explicit) return explicit.toLowerCase();
  const parts = splitDisplayName(getDisplayName(student));
  return (parts.at(-1) ?? '').toLowerCase();
}

/** Prefer explicit firstName; otherwise first token of the display name. */
export function getAttendanceSortFirstName(student: NameParts): string {
  const explicit = student.firstName?.trim();
  if (explicit) return explicit.toLowerCase();
  const parts = splitDisplayName(getDisplayName(student));
  return (parts[0] ?? '').toLowerCase();
}

export type AttendanceNameSortField = 'lastName' | 'firstName';

/**
 * Trial roster rows store GHL contact first/last (often the parent) separately
 * from the child display name (`studentName` / `name`). Sort using the name
 * shown in the UI so first/last toggles match what admins see.
 */
export function toTrialAttendanceSortParts(student: NameParts): NameParts {
  const display = student.studentName?.trim() || student.name?.trim() || '';
  return { name: display, studentName: display };
}

export function compareAttendanceStudentsByName(
  a: NameParts,
  b: NameParts,
  field: AttendanceNameSortField = 'lastName',
): number {
  const primaryA =
    field === 'lastName'
      ? getAttendanceSortLastName(a)
      : getAttendanceSortFirstName(a);
  const primaryB =
    field === 'lastName'
      ? getAttendanceSortLastName(b)
      : getAttendanceSortFirstName(b);
  const primary = primaryA.localeCompare(primaryB);
  if (primary !== 0) return primary;

  const secondaryA =
    field === 'lastName'
      ? getAttendanceSortFirstName(a)
      : getAttendanceSortLastName(a);
  const secondaryB =
    field === 'lastName'
      ? getAttendanceSortFirstName(b)
      : getAttendanceSortLastName(b);
  return secondaryA.localeCompare(secondaryB);
}
